/**
 * Convenção do arquivo diário de log (spec/12-APPLICATION-LOGS.md §2).
 * O nome usa dia-mês-ano em pt-BR, não ISO: `logs-dia-10-08-2026.json`.
 */

export const LOG_FILE_PREFIX = 'logs-dia-';
export const LOG_FILE_SUFFIX = '.json';
export const DEFAULT_LOG_TIMEZONE = 'America/Sao_Paulo';

export interface CivilDate {
  day: string;
  month: string;
  year: string;
}

/** Quebra um instante no dia civil do timezone informado, sem depender de lib externa. */
export function civilDateIn(date: Date, timeZone: string = DEFAULT_LOG_TIMEZONE): CivilDate {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);

  const pick = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((p) => p.type === type)?.value ?? '';

  return { day: pick('day'), month: pick('month'), year: pick('year') };
}

/** `2026-08-10T23:15Z` em São Paulo → `logs-dia-10-08-2026.json`. */
export function resolveLogFileName(date: Date, timeZone: string = DEFAULT_LOG_TIMEZONE): string {
  const { day, month, year } = civilDateIn(date, timeZone);
  return `${LOG_FILE_PREFIX}${day}-${month}-${year}${LOG_FILE_SUFFIX}`;
}

/** `10-08-2026` (o formato aceito pela query `?date=` da API de logs). */
export function resolveLogDateKey(date: Date, timeZone: string = DEFAULT_LOG_TIMEZONE): string {
  const { day, month, year } = civilDateIn(date, timeZone);
  return `${day}-${month}-${year}`;
}

export function logFileNameForDateKey(dateKey: string): string {
  if (!isValidLogDateKey(dateKey)) {
    throw new Error(`data inválida para log: "${dateKey}" (esperado DD-MM-YYYY)`);
  }
  return `${LOG_FILE_PREFIX}${dateKey}${LOG_FILE_SUFFIX}`;
}

export function isValidLogDateKey(dateKey: string): boolean {
  return /^\d{2}-\d{2}-\d{4}$/.test(dateKey);
}

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** `YYYY-MM-DD` (input type=date) → `DD-MM-YYYY` da API de logs. */
export function dateKeyFromIsoDate(isoDate: string): string | null {
  const match = ISO_DATE.exec(isoDate);
  if (!match) return null;
  return `${match[3]}-${match[2]}-${match[1]}`;
}

/** `DD-MM-YYYY` → `YYYY-MM-DD` para o date picker. */
export function isoDateFromDateKey(dateKey: string): string | null {
  if (!isValidLogDateKey(dateKey)) return null;
  const [day, month, year] = dateKey.split('-');
  return `${year}-${month}-${day}`;
}

export function dateKeyFromLogFileName(filename: string): string | null {
  const match = /^logs-dia-(\d{2}-\d{2}-\d{4})\.json$/.exec(filename);
  return match ? match[1] : null;
}

/**
 * Timestamp do arquivo de log (spec/12 §4): ISO-8601 no fuso configurado,
 * com offset — nunca `Z`. Ex.: `2026-08-10T20:15:33.421-03:00`.
 */
export function formatTimestampInZone(
  date: Date,
  timeZone: string = DEFAULT_LOG_TIMEZONE,
): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
    timeZoneName: 'longOffset',
  }).formatToParts(date);

  const pick = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((part) => part.type === type)?.value ?? '';

  const millis = date.getMilliseconds().toString().padStart(3, '0');
  return `${pick('year')}-${pick('month')}-${pick('day')}T${pick('hour')}:${pick('minute')}:${pick('second')}.${millis}${normalizeUtcOffset(pick('timeZoneName'))}`;
}

function normalizeUtcOffset(timeZoneName: string): string {
  if (!timeZoneName || timeZoneName === 'GMT' || timeZoneName === 'UTC') return '+00:00';
  const match = /(?:GMT|UTC)([+-])(\d{1,2})(?::?(\d{2}))?/.exec(timeZoneName);
  if (!match) return '+00:00';
  return `${match[1]}${match[2].padStart(2, '0')}:${(match[3] ?? '00').padStart(2, '0')}`;
}
